// S9 — per-camera health detail page.
//
// Top: name + status badge + last-checked + Poll Now.
// Metrics row: today uptime, recording %, latest bitrate, consecutive
// failures.
// 7-day uptime bar chart (recharts).
// Active alerts list with resolve.
// Health log table (paginated client-side).
import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ArrowLeftIcon, RotateCwIcon } from "lucide-react";

import {
  useCameraHealthDetail,
  useCameraHealthHistory,
  usePollCamera,
  useResolveAlert,
} from "@/api/health";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { QueryState } from "@/components/QueryState";
import { useFormatTimestamp } from "@/utils/datetime";


function uptimeColor(pct: number): string {
  if (pct >= 80) return "#10b981"; // emerald
  if (pct >= 60) return "#f59e0b"; // amber
  return "#ef4444"; // red
}

function statusBadge(s: string) {
  if (s === "online") return <Badge variant="online">ONLINE</Badge>;
  if (s === "offline") return <Badge variant="error">OFFLINE</Badge>;
  return <Badge variant="outline">{s.toUpperCase()}</Badge>;
}


export default function CameraHealthDetail() {
  const { cameraId } = useParams<{ cameraId: string }>();
  const detail = useCameraHealthDetail(cameraId ?? null);
  const history = useCameraHealthHistory(cameraId ?? null, 200);
  const poll = usePollCamera();
  const resolve = useResolveAlert();
  const formatTs = useFormatTimestamp();

  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;
  const allLog = history.data?.items ?? [];
  const pageItems = allLog.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(allLog.length / PAGE_SIZE));

  if (!cameraId) {
    return <p className="text-sm">Missing camera id.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/health"
            className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeftIcon className="mr-1 h-3 w-3" /> Back to Health
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">
            {detail.data?.name ?? cameraId.slice(0, 8)}
          </h1>
          {detail.data?.entity_name && (
            <p className="text-sm text-muted-foreground">
              {detail.data.entity_name}
            </p>
          )}
          <div className="mt-1 flex items-center gap-3 text-xs">
            {detail.data && statusBadge(detail.data.status)}
            <span className="text-muted-foreground">
              Last check:{" "}
              {detail.data?.last_health_check_at
                ? formatTs(detail.data.last_health_check_at, "relative")
                : "never"}
            </span>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => poll.mutate(cameraId)}
          disabled={poll.isPending}
        >
          <RotateCwIcon className="mr-1 h-3.5 w-3.5" /> Poll Now
        </Button>
      </div>

      {/* THE WHOLE DETAIL BODY IS GATED ON THE DETAIL FETCH.
          Every read below is `detail.data?.x ?? <fallback>`, and the fallbacks
          are REASSURING: "Consecutive Failures 0", "No uptime data yet", and an
          Active Alerts card that renders only when the array is non-empty. On a
          500 that composed into a page stating this camera has zero failures
          and zero alerts — a positive health claim built entirely out of
          absences. Empty and error may not render alike; here they were the
          same pixels. There is no isEmpty: a camera detail either resolves or
          it does not. */}
      <QueryState
        isLoading={detail.isLoading}
        isError={detail.isError}
        error={detail.error}
        label="this camera's health"
        onRetry={() => void detail.refetch()}
      >
      {/* Metrics row */}
      <div className="grid gap-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">
              Uptime Today
            </p>
            <p
              className="mt-1 text-2xl font-semibold"
              style={{ color: detail.data
                ? uptimeColor(detail.data.today_uptime_pct)
                : undefined }}
            >
              {detail.data ? `${detail.data.today_uptime_pct.toFixed(1)}%` : "—"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">
              Recording (today)
            </p>
            <p className="mt-1 text-2xl font-semibold">
              {detail.data?.recording_compliance_today_pct != null
                ? `${detail.data.recording_compliance_today_pct.toFixed(1)}%`
                : "—"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">
              Bitrate (latest)
            </p>
            <p className="mt-1 text-2xl font-semibold">
              {detail.data?.latest && "bitrate_kbps" in detail.data.latest
                ? `${(detail.data.latest as Record<string, number | null>).bitrate_kbps ?? "—"} kbps`
                : "—"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">
              Consecutive Failures
            </p>
            <p
              className={`mt-1 text-2xl font-semibold ${
                (detail.data?.consecutive_failures ?? 0) > 0
                  ? "text-red-600"
                  : ""
              }`}
            >
              {detail.data?.consecutive_failures ?? 0}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* 7-day trend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">7-Day Uptime Trend</CardTitle>
        </CardHeader>
        <CardContent>
          {detail.data && detail.data.seven_day_trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={detail.data.seven_day_trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 10 }}
                  unit="%"
                />
                <Tooltip
                  formatter={(v) => [
                    `${typeof v === "number" ? v.toFixed(1) : v}%`,
                    "uptime",
                  ]}
                />
                <Bar dataKey="uptime_pct">
                  {detail.data.seven_day_trend.map((d, i) => (
                    <Cell key={i} fill={uptimeColor(d.uptime_pct)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No uptime data yet — the daily summarizer hasn't run for this
              camera.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Active alerts */}
      {detail.data && detail.data.active_alerts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Active Alerts</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Severity</th>
                  <th className="px-3 py-2 text-left">Message</th>
                  <th className="px-3 py-2 text-left">Last Seen</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {detail.data.active_alerts.map((a) => (
                  <tr key={a.id} className="border-b last:border-0">
                    <td className="px-3 py-2 text-xs">{a.alert_type}</td>
                    <td className="px-3 py-2">
                      <Badge
                        variant={
                          a.severity === "critical"
                            ? "error"
                            : a.severity === "warning"
                              ? "warning"
                              : "outline"
                        }
                      >
                        {a.severity}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-xs">{a.message}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {a.last_seen_at
                        ? formatTs(a.last_seen_at, "relative")
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resolve.mutate(a.id)}
                      >
                        Resolve
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
      </QueryState>

      {/* Health log table. SEPARATE query, so a separate gate: the log can fail
          while the detail resolves, and "no health log entries yet" is a real
          answer that must not stand in for a failed request. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Health Log</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <QueryState
            isLoading={history.isLoading}
            isError={history.isError}
            error={history.error}
            label="the health log"
            onRetry={() => void history.refetch()}
            isEmpty={pageItems.length === 0}
            emptyMessage="No health log entries yet."
          >
            <>
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">Checked At</th>
                    <th className="px-3 py-2 text-left">Online</th>
                    <th className="px-3 py-2 text-left">Recording</th>
                    <th className="px-3 py-2 text-right">Bitrate</th>
                    <th className="px-3 py-2 text-right">FPS</th>
                    <th className="px-3 py-2 text-right">HDD %</th>
                    <th className="px-3 py-2 text-left">Snapshot</th>
                    <th className="px-3 py-2 text-left">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((r, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="px-3 py-2 text-xs">
                        {r.checked_at ? formatTs(r.checked_at) : "—"}
                      </td>
                      <td className="px-3 py-2">
                        {r.is_online ? (
                          <Badge variant="online">yes</Badge>
                        ) : (
                          <Badge variant="error">no</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {r.is_recording ? "yes" : "no"}
                      </td>
                      <td className="px-3 py-2 text-right text-xs">
                        {r.bitrate_kbps ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-xs">
                        {r.fps != null ? r.fps.toFixed(1) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-xs">
                        {r.hdd_used_pct != null
                          ? `${r.hdd_used_pct.toFixed(0)}%`
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {r.snapshot_ok == null
                          ? "—"
                          : r.snapshot_ok
                            ? "ok"
                            : "fail"}
                      </td>
                      <td className="px-3 py-2 text-xs text-red-700">
                        {r.error_message ?? ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t bg-muted/30 px-3 py-2 text-xs">
                  <span className="text-muted-foreground">
                    Page {page + 1} of {totalPages}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={page === 0}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Prev
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          </QueryState>
        </CardContent>
      </Card>
    </div>
  );
}
